import type { IBackendCallParticipant, ICallParticipant } from './state'

/** Backend user participant → the participant model the call UI already reads */
export const adaptBackendCallParticipant = (
  participant: IBackendCallParticipant
): ICallParticipant => {
  const info = participant.userData?.info
  const name = [info?.firstName, info?.lastName].filter(Boolean).join(' ')

  return {
    number: participant.userData?.identifier ?? '',
    ...(name ? { name } : {}),
    ...(info?.avatar ? { avatarUrl: info.avatar } : {}),
    uniqId: String(participant.userId ?? participant.id),
  }
}

export function genCharArray(charA: string, charZ: string) {
  const a = []
  let i = charA.charCodeAt(0)
  const j = charZ.charCodeAt(0)

  for (; i <= j; ++i) {
    a.push(String.fromCharCode(i))
  }

  return a
}

/** Only `kind === 'user'`. Customer and fallback stay in `backendCall.participants` */
export const adaptBackendCallParticipants = (
  participants: IBackendCallParticipant[]
): ICallParticipant[] =>
  participants
    .filter((participant) => participant.kind === 'user')
    .map(adaptBackendCallParticipant)

const isCurrentUser = (
  participant: IBackendCallParticipant,
  callSid: string | null,
  currentUserId?: number | null
) => {
  if (participant.kind !== 'user') return false
  if (currentUserId != null && participant.userId === currentUserId) return true

  return Boolean(callSid) && participant.callSid === callSid
}

/**
 * Keeps the object the call was started with and appends other users.
 * The current user is not a shown participant.
 */
export const mergePresentedParticipants = (
  previous: ICallParticipant[],
  backendParticipants: IBackendCallParticipant[],
  options: { callSid: string | null; currentUserId?: number | null }
): ICallParticipant[] => {
  const userKeys = new Set(
    backendParticipants
      .filter((participant) => participant.kind === 'user')
      .map((participant) => String(participant.userId ?? participant.id))
  )
  const original = previous.filter(
    (participant) => !participant.uniqId || !userKeys.has(participant.uniqId)
  )
  const added = backendParticipants
    .filter(
      (participant) =>
        participant.kind === 'user' &&
        !isCurrentUser(participant, options.callSid, options.currentUserId)
    )
    .map(adaptBackendCallParticipant)

  return [...original, ...added]
}
